import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Select, Table, Typography, message, Empty, theme,
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  RiseOutlined,
  BarChartOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import { ventaService, eventoService } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title } = Typography;
const { Option } = Select;

const money = (v) => `$${Number(v || 0).toLocaleString('es-AR')}`;

const TableroVentas = () => {
  const { token } = theme.useToken();
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState(null); // null = todos los eventos
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState(null);

  // Cargar eventos (lista liviana) para el selector.
  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const res = await eventoService.getListaCorta();
        setEventos(eventosDeListaCorta(res));
      } catch {
        message.error('Error al cargar eventos');
      } finally {
        setLoadingEventos(false);
      }
    };
    fetchEventos();
  }, []);

  const fetchTablero = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await ventaService.getTablero(id);
      setDatos(res.data);
    } catch {
      message.error('Error al cargar el tablero de ventas');
      setDatos(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTablero(eventoId);
  }, [eventoId, fetchTablero]);

  const colsCategoria = [
    { title: 'Categoría', dataIndex: 'nom_categoria', key: 'nom_categoria', width: 200, ellipsis: true, render: (v) => v || '—' },
    { title: 'Ventas', dataIndex: 'ventas', key: 'ventas', align: 'right', width: 80 },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 130, render: money },
    { title: 'Promedio', dataIndex: 'promedio', key: 'promedio', align: 'right', width: 130, render: money },
    { title: 'Máximo', dataIndex: 'maximo', key: 'maximo', align: 'right', width: 130, render: money },
  ];

  const colsCorral = [
    { title: 'Corral', dataIndex: 'corral', key: 'corral', width: 90, render: (v) => v ?? '—' },
    { title: 'Ventas', dataIndex: 'ventas', key: 'ventas', align: 'right', width: 80 },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', render: money },
  ];

  const colsCompradores = [
    { title: '#', render: (_, __, i) => i + 1, width: 40 },
    { title: 'Comprador', dataIndex: 'nom_usuario', key: 'nom_usuario', width: 200, ellipsis: true, render: (v) => v || '—' },
    { title: 'Compras', dataIndex: 'ventas', key: 'ventas', align: 'right', width: 90 },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', render: money },
  ];

  const tableProps = {
    size: 'small',
    pagination: false,
    // `x: 'max-content'` lo necesita colsCategoria: sus anchos fijos suman 470px
    // (80+130+130+130) mas la columna elastica, o sea que en un telefono de
    // 375px los importes quedaban aplastados. Las otras dos tablas suman menos
    // que el ancho de pantalla y no cambian: rc-table pone `minWidth: 100%`
    // junto al `max-content`, asi que mientras el contenedor sea mas ancho que
    // el contenido la tabla se estira igual que siempre.
    scroll: { x: 'max-content', y: 260 },
    style: { fontSize: 13 },
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 10, color: token.colorPrimary }} />
            Tablero de Ventas
          </Title>
        </Col>
        <Col>
          <Select
            placeholder={loadingEventos ? 'Cargando eventos...' : 'Todos los eventos'}
            style={{ minWidth: 280 }}
            value={eventoId}
            onChange={setEventoId}
            disabled={loadingEventos}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {eventos.map((e) => (
              <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>
            ))}
          </Select>
        </Col>
      </Row>

      {loading ? (
        <PujaSpinner size="lg" />
      ) : !datos ? null : (
        <>
          {/* KPI cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card variant="borderless" style={{ background: token.colorSuccessBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Total facturado"
                  value={datos.total_importe}
                  prefix={<DollarOutlined />}
                  formatter={(v) => Number(v).toLocaleString('es-AR')}
                  valueStyle={{ color: token.colorSuccess, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card variant="borderless" style={{ background: token.colorPrimaryBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Ventas registradas"
                  value={datos.total_ventas}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: token.colorPrimary, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card variant="borderless" style={{ background: token.colorInfoBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Animales vendidos"
                  value={datos.animales_vendidos}
                  prefix={<TagsOutlined />}
                  valueStyle={{ color: token.colorInfo, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card variant="borderless" style={{ background: token.colorWarningBg, borderRadius: token.borderRadiusLG }}>
                <Statistic
                  title="Promedio por venta"
                  value={datos.promedio_general}
                  prefix={<RiseOutlined />}
                  formatter={(v) => Number(v).toLocaleString('es-AR')}
                  valueStyle={{ color: token.colorWarning, fontWeight: 700 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Ventas por categoría (promedio + máximo) */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card
                title="Ventas por categoría (promedio y máximo)"
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.por_categoria}
                  columns={colsCategoria}
                  rowKey="nom_categoria"
                  locale={{ emptyText: <Empty description="Sin ventas registradas" /> }}
                />
              </Card>
            </Col>
          </Row>

          {/* Por corral / Top compradores */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                title="Ventas por corral"
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.por_corral}
                  columns={colsCorral}
                  rowKey="corral"
                  locale={{ emptyText: 'Sin ventas por corral' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={<span><TrophyOutlined style={{ color: '#faad14', marginRight: 6 }} />Top compradores</span>}
                variant="borderless"
                style={{ borderRadius: token.borderRadiusLG }}
              >
                <Table
                  {...tableProps}
                  dataSource={datos.top_compradores}
                  columns={colsCompradores}
                  rowKey="nom_usuario"
                  locale={{ emptyText: 'Sin compradores' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default TableroVentas;
